import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { TvZoomService } from './services/tv-zoom.service';

@Component({
  selector: 'app-root',
  template: `
    <notifier-container></notifier-container>

    <!-- Global loading overlay: driven by LoadingInterceptor. Whenever any
         HTTP call is in flight that hasn't opted out, this dim backdrop is
         shown over the whole viewport with a bread-baking animation. The
         backdrop and high z-index prevent click-through so users can't act
         on half-loaded data. -->
    <ngx-spinner
      bdColor="rgba(38, 26, 18, 0.78)"
      [fullScreen]="true"
      type=""
      size="default">
      <div class="bread-loader">
        <div class="steam">
          <span class="puff p1">·</span>
          <span class="puff p2">·</span>
          <span class="puff p3">·</span>
        </div>
        <div class="oven">
          <div class="loaf">🍞</div>
          <div class="flames">
            <span class="flame f1"></span>
            <span class="flame f2"></span>
            <span class="flame f3"></span>
          </div>
        </div>
        <div class="caption">Baking your data&hellip;</div>
      </div>
    </ngx-spinner>

    <router-outlet></router-outlet>

    <!-- In-app confirm / prompt dialogs (LbConfirmService) -->
    <lb-confirm-host></lb-confirm-host>

    <!-- Floating switcher for paired Android TV devices. Shown only when
         this browser is a paired TV and on /factory-tv or /factory-display. -->
    <div *ngIf="showTvSwitcher" class="tv-switcher">
      <!-- Always-visible zoom buttons. Sit to the left of the ☰ menu so an
           operator with a mouse can resize without opening any submenu. -->
      <button class="tv-zoom-btn" (click)="zoomOut()" title="Zoom out">−</button>
      <button class="tv-zoom-btn tv-zoom-reset" (click)="zoomReset()" title="Reset zoom">
        {{ zoomPercent }}%
      </button>
      <button class="tv-zoom-btn" (click)="zoomIn()" title="Zoom in">+</button>
      <button class="tv-switcher-toggle" (click)="tvSwitcherOpen = !tvSwitcherOpen">☰</button>
      <div *ngIf="tvSwitcherOpen" class="tv-switcher-panel">
        <div class="tv-switcher-title">Switch view</div>
        <button class="tv-switcher-btn" (click)="goTo('factory-tv')"
                [class.active]="currentRoute === '/factory-tv'">
          <span class="tv-switcher-emoji">📺</span> Factory TV
        </button>
        <button class="tv-switcher-btn" (click)="goTo('factory-display')"
                [class.active]="currentRoute === '/factory-display'">
          <span class="tv-switcher-emoji">🏭</span> Factory Display
        </button>
        <hr class="tv-switcher-sep" />
        <button class="tv-switcher-btn ghost" (click)="goTo('tv-app')">Picker / settings</button>
        <button class="tv-switcher-btn danger" (click)="unpair()">Unpair this TV</button>
      </div>
    </div>
  `,
  styles: [`
    /* Custom ngx-spinner template: a tiny oven that bakes a loaf while data
       is in flight. Pure CSS keyframes — no extra assets, no library beyond
       what's already shipped. */
    .bread-loader {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.6rem;
      color: #fff5e0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      letter-spacing: 0.04em;
      text-shadow: 0 1px 2px rgba(0,0,0,0.4);
    }

    /* Three little puffs of steam that rise and fade. Staggered so the
       motion looks organic instead of marching in lockstep. */
    .steam {
      display: flex;
      gap: 0.5rem;
      height: 1.6rem;
      align-items: flex-end;
      font-size: 1.6rem;
      line-height: 1;
    }
    .steam .puff {
      display: inline-block;
      opacity: 0;
      animation: rise 1.8s ease-out infinite;
      color: rgba(255, 245, 224, 0.85);
    }
    .steam .puff.p1 { animation-delay: 0s; }
    .steam .puff.p2 { animation-delay: 0.4s; }
    .steam .puff.p3 { animation-delay: 0.8s; }

    @keyframes rise {
      0%   { transform: translateY(0) scale(0.6); opacity: 0; }
      30%  { opacity: 0.9; }
      100% { transform: translateY(-1.4rem) scale(1.4); opacity: 0; }
    }

    /* The oven is a rounded brown box; the loaf sits inside and bobs as if
       rising. Box-shadow adds a soft inner glow so the loaf looks lit. */
    .oven {
      position: relative;
      width: 5.5rem;
      height: 4rem;
      background: linear-gradient(180deg, #6b3a1a 0%, #4a2410 100%);
      border: 0.18rem solid #2d1607;
      border-radius: 0.55rem 0.55rem 0.4rem 0.4rem;
      box-shadow:
        inset 0 0 1.4rem rgba(255, 140, 40, 0.55),
        0 0.25rem 0.5rem rgba(0,0,0,0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    /* A thin glowing strip at the bottom that pulses — the "fire" */
    .oven::after {
      content: '';
      position: absolute;
      left: 6%;
      right: 6%;
      bottom: 0.18rem;
      height: 0.32rem;
      border-radius: 0.18rem;
      background: linear-gradient(90deg, #ffb74d, #ff7043, #ffb74d);
      box-shadow: 0 0 0.7rem rgba(255, 140, 40, 0.9);
      animation: ember 1.2s ease-in-out infinite alternate;
    }
    @keyframes ember {
      0%   { opacity: 0.7; transform: scaleX(0.94); }
      100% { opacity: 1;   transform: scaleX(1.04); }
    }

    /* The loaf bobs upward subtly to mimic dough rising in the oven. */
    .loaf {
      font-size: 2.4rem;
      line-height: 1;
      filter: drop-shadow(0 0.1rem 0.25rem rgba(0,0,0,0.5));
      animation: bake 1.8s ease-in-out infinite;
      transform-origin: center bottom;
    }
    @keyframes bake {
      0%, 100% { transform: translateY(0.1rem) scale(0.95); }
      50%      { transform: translateY(-0.18rem) scale(1.06); }
    }

    /* Flame flicker just behind the loaf — small coloured dots that scale
       up/down independently for that fire-flicker feel. Hidden visually
       behind the loaf via z-index ordering. */
    .flames {
      position: absolute;
      bottom: 0.5rem;
      display: flex;
      gap: 0.45rem;
      z-index: -1;
    }
    .flame {
      width: 0.5rem;
      height: 0.7rem;
      background: radial-gradient(circle at 50% 70%, #fff176 0%, #ff9800 40%, transparent 75%);
      border-radius: 50% 50% 35% 35%;
      opacity: 0.85;
      animation: flicker 0.6s ease-in-out infinite alternate;
    }
    .flame.f1 { animation-delay: 0s; }
    .flame.f2 { animation-delay: 0.15s; }
    .flame.f3 { animation-delay: 0.3s; }
    @keyframes flicker {
      0%   { transform: scaleY(0.8); opacity: 0.7; }
      100% { transform: scaleY(1.2); opacity: 1; }
    }

    .caption {
      font-size: 0.95rem;
      font-weight: 500;
      opacity: 0.92;
      animation: pulse 1.6s ease-in-out infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 0.75; }
      50%      { opacity: 1; }
    }
    .tv-switcher { position: fixed; bottom: 20px; right: 20px; z-index: 9999;
      font-family: -apple-system, Segoe UI, Roboto, sans-serif;
      display: flex; align-items: center; gap: 8px; }
    .tv-zoom-btn { height: 44px; min-width: 44px; padding: 0 12px;
      border-radius: 22px; background: rgba(15,23,42,0.85); color: #fff;
      border: 2px solid rgba(255,255,255,0.25); font-size: 1.3rem; font-weight: 700;
      cursor: pointer; box-shadow: 0 8px 20px rgba(0,0,0,0.5); line-height: 1; }
    .tv-zoom-btn.tv-zoom-reset { font-size: 0.9rem; min-width: 56px; }
    .tv-zoom-btn:hover { background: rgba(30,41,59,0.95); }
    .tv-switcher-toggle { width: 56px; height: 56px; border-radius: 50%;
      background: rgba(15,23,42,0.85); color: #fff; border: 2px solid rgba(255,255,255,0.25);
      font-size: 1.5rem; cursor: pointer; box-shadow: 0 8px 20px rgba(0,0,0,0.5); }
    .tv-switcher-toggle:hover { background: rgba(30,41,59,0.95); transform: scale(1.05); }
    .tv-switcher-panel { position: absolute; bottom: 68px; right: 0; min-width: 260px;
      background: #0F172A; color: #F1F5F9; padding: 14px; border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 20px 40px rgba(0,0,0,0.5); }
    .tv-switcher-title { font-size: 0.85rem; color: #94A3B8; text-transform: uppercase;
      letter-spacing: 2px; margin-bottom: 10px; padding: 0 4px; }
    .tv-switcher-btn { display: flex; align-items: center; gap: 10px; width: 100%;
      padding: 12px 14px; background: transparent; color: #F1F5F9; border: 1px solid transparent;
      border-radius: 8px; font-size: 1rem; cursor: pointer; text-align: left; }
    .tv-switcher-btn:hover { background: rgba(255,255,255,0.08); }
    .tv-switcher-btn.active { background: rgba(59,130,246,0.18);
      border-color: rgba(59,130,246,0.5); color: #BFDBFE; }
    .tv-switcher-btn.ghost { color: #94A3B8; font-size: 0.9rem; }
    .tv-switcher-btn.danger { color: #FCA5A5; font-size: 0.9rem; }
    .tv-switcher-emoji { font-size: 1.2rem; }
    .tv-switcher-sep { margin: 8px 0; border: none; border-top: 1px solid rgba(255,255,255,0.1); }
  `]
})
export class AppComponent implements OnInit {
  title = 'Lucky Bread Order Management';
  currentRoute = '';
  tvSwitcherOpen = false;
  zoomLevel = 1.0;

  constructor(
    private router: Router,
    private auth: AuthService,
    private zoom: TvZoomService
  ) { }

  ngOnInit(): void {
    // Mirror the zoom service into a local field so the template can bind
    // to it directly without async pipes (cleaner inside *ngIf chains).
    this.zoom.zoom.subscribe(z => (this.zoomLevel = z));

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.currentRoute = (e.urlAfterRedirects || e.url || '').split('?')[0];
        this.tvSwitcherOpen = false;
        this.applyTvScrollLock();
      });
  }

  get showTvSwitcher(): boolean {
    if (!this.auth.isTvDevice()) return false;
    return this.currentRoute === '/factory-tv' || this.currentRoute === '/factory-display';
  }

  get zoomPercent(): number { return Math.round(this.zoomLevel * 100); }

  zoomIn(): void { this.zoom.zoomIn(); }
  zoomOut(): void { this.zoom.zoomOut(); }
  zoomReset(): void { this.zoom.reset(); }

  /** Disable body scrolling on the factory views when this is a paired TV —
   *  the user is meant to zoom-to-fit, not scroll. */
  private applyTvScrollLock(): void {
    const isFactoryView =
      this.currentRoute === '/factory-tv' || this.currentRoute === '/factory-display';
    const lock = this.auth.isTvDevice() && isFactoryView;
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  goTo(route: string): void { this.router.navigate(['/' + route]); }

  unpair(): void {
    if (!confirm('Unpair this TV? You will need to scan a QR again to use it.')) return;
    this.auth.clearSession();
    this.router.navigate(['/tv-app']);
  }
}
